/**
 * Manutenção da Tabela Fipe — a barra, o dicionário de campos e a normalização.
 *
 * A consulta à Fipe fica em **Mais › Tabelas › Fipe**. Aqui é o trabalho de
 * arrumar o acervo, que é outra coisa e outro gesto.
 *
 * **A barra é um acervo finito, e a tela mostra o fim.** A Fipe às vezes descreve
 * duas variantes num registro só — `"Legend 3.2/3.5"` — e alguém precisa
 * separá-las. São **586 pendentes de 618** em produção; é um trabalho que acaba,
 * e saber quanto falta é metade do motivo para começar.
 *
 * **Nada é gravado até confirmar.** A API propõe a divisão, quem revisa corrige o
 * texto de cada variante, e só então grava. É o mesmo desenho de conferir/aplicar
 * do resto do sistema.
 *
 * **A normalização mexe em ~6.900 registros de uma vez.** Recalcula cilindrada,
 * válvulas, turbo, potência e nome curto a partir do texto do veículo, usando o
 * dicionário da aba ao lado. Por isso pede confirmação e diz o tamanho antes.
 */

import { useEffect, useMemo, useState } from "react"
import {
  usarCamposDaFipe, usarCandidatoDaBarra, usarConfirmarBarra, usarExcluirCampoDaFipe,
  usarFipePorVeiculoAno, usarNormalizarFipe, usarRegistrosComBarra, usarSalvarCampoDaFipe,
  type CampoDaFipe, type RegistroFipe, type VarianteDaBarra,
} from "@/api/manutencaoFipe"
import { AvisoDeDadoGlobal } from "@/kit/AvisoDeDadoGlobal"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Faixa } from "@/kit/Faixa"
import { Aviso, Botao, Campo, estiloCampo, mensagem } from "../outros/pecas"

type Aba = "barra" | "campos" | "veiculo-ano"

export function ManutencaoFipe() {
  const [aba, setAba] = useState<Aba>("barra")

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Faixa titulo="Manutenção da Tabela Fipe" detalhe="Arrumar o acervo — a consulta fica em Tabelas › Fipe" />

      <AvisoDeDadoGlobal oQueMuda="a Tabela Fipe inteira, que casa veículo em todos os projetos" />

      <div style={{ display: "flex", gap: 4, padding: "10px 18px 0", borderBottom: "1px solid var(--line2)" }}>
        {([
          ["barra", "Analisar barra"],
          ["campos", "Dicionário de campos"],
          ["veiculo-ano", "Veículo / ano"],
        ] as [Aba, string][]).map(([chave, rotulo]) => (
          <button key={chave} onClick={() => setAba(chave)} style={{
            border: 0, borderBottom: "2px solid " + (aba === chave ? "var(--acao)" : "transparent"),
            background: "transparent", cursor: "pointer", padding: "6px 12px", fontSize: 12.5,
            color: aba === chave ? "var(--txt)" : "var(--txt2)",
            fontWeight: aba === chave ? 600 : 400,
          }}>
            {rotulo}
          </button>
        ))}
      </div>

      {aba === "barra" ? <Barra /> : aba === "campos" ? <Campos /> : <VeiculoAno />}
    </div>
  )
}

// ------------------------------------------------------------------ barra

function Barra() {
  const [todos, setTodos] = useState(false)
  const [busca, setBusca] = useState("")
  const [aberto, setAberto] = useState(0)
  const [recado, setRecado] = useState<string | null>(null)
  const [confirmandoNormalizar, setConfirmandoNormalizar] = useState(false)

  const lista = usarRegistrosComBarra(todos)
  const normalizar = usarNormalizarFipe()

  const itens = useMemo(() => {
    const t = busca.trim().toLowerCase()
    const todosItens = lista.data ?? []
    if (!t) return todosItens
    return todosItens.filter((i) => `${i.marca} ${i.veiculo} ${i.fipeCodigo}`.toLowerCase().includes(t))
  }, [lista.data, busca])

  const pendentes = (lista.data ?? []).filter((i) => !i.conferido).length

  return (
    <>
      <div style={{
        display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center",
        padding: "12px 18px", borderBottom: "1px solid var(--line2)",
      }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--txt2)" }}>
          <input type="checkbox" checked={todos} onChange={(e) => { setTodos(e.target.checked); setAberto(0) }} />
          Incluir os já tratados
        </label>
        <input value={busca} onChange={(e) => setBusca(e.target.value)}
               placeholder="marca, veículo ou código…" style={{ ...estiloCampo, width: 260 }} />

        <Botao aoClicar={() => setConfirmandoNormalizar(true)} ocupado={normalizar.isPending}>
          {normalizar.isPending ? "Normalizando…" : "Normalizar a tabela"}
        </Botao>
      </div>

      {confirmandoNormalizar && (
        <div style={{ padding: "10px 18px 0" }}>
          <div style={{ padding: 10, borderRadius: 6, border: "1px solid var(--erro)" }}>
            <p style={{ margin: "0 0 8px", fontSize: 11.5, lineHeight: 1.55 }}>
              A normalização recalcula <b>ciclo, cilindrada, válvulas, perfil, turbo, transmissão,
              tração e nome curto</b> de <b>toda</b> a Tabela Fipe — cerca de 6.900 registros — a
              partir do texto do veículo e do dicionário da aba ao lado. Quem consulta a Fipe vê o
              resultado imediatamente, em todos os projetos.
            </p>
            <div style={{ display: "flex", gap: 6 }}>
              <Botao
                destaque
                aoClicar={() => {
                  setConfirmandoNormalizar(false)
                  setRecado(null)
                  normalizar.mutate(undefined, {
                    onSuccess: (r) => setRecado(
                      `${r.atualizadas.toLocaleString("pt-BR")} de ${r.processadas.toLocaleString("pt-BR")} atualizadas` +
                      (r.erros > 0 ? ` · ${r.erros} com erro` : "")),
                    onError: (e) => setRecado(mensagem(e)),
                  })
                }}
              >
                Normalizar mesmo assim
              </Botao>
              <Botao aoClicar={() => setConfirmandoNormalizar(false)}>Cancelar</Botao>
            </div>
          </div>
        </div>
      )}

      {recado && <div style={{ padding: "8px 18px 0" }}><Aviso>{recado}</Aviso></div>}

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <div style={{
          flex: "1 1 auto", minWidth: 0, overflow: "auto", padding: 14,
          borderRight: aberto ? "1px solid var(--line2)" : undefined,
        }}>
          {lista.isPending ? (
            <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Carregando…</p>
          ) : lista.error ? (
            <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(lista.error)}</p>
          ) : itens.length === 0 ? (
            <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Nada aqui.</p>
          ) : (
            <div style={{ border: "1px solid var(--line2)", borderRadius: 6, overflow: "hidden" }}>
              {itens.map((r) => (
                <LinhaComBarra key={r.id} r={r} ativo={aberto === r.id}
                               aoAbrir={() => { setAberto(aberto === r.id ? 0 : r.id); setRecado(null) }} />
              ))}
            </div>
          )}
        </div>

        {aberto > 0 && (
          <Divisao id={aberto} aoFechar={() => setAberto(0)} aoAvisar={setRecado} />
        )}
      </div>

      <BarraDeEstado direita={lista.data ? `${lista.data.length} com barra` : undefined}>
        {lista.data
          ? `${pendentes} ainda sem tratar — é um trabalho que acaba.`
          : "A Fipe às vezes descreve duas variantes num registro só."}
      </BarraDeEstado>
    </>
  )
}

function LinhaComBarra({ r, ativo, aoAbrir }: { r: RegistroFipe; ativo: boolean; aoAbrir: () => void }) {
  return (
    <div onClick={aoAbrir} style={{
      display: "flex", alignItems: "center", gap: 10, padding: "5px 10px", cursor: "pointer",
      borderBottom: "1px solid var(--line2)", fontSize: 11.5,
      background: ativo ? "var(--surface2)" : undefined,
    }}>
      <span className="mono" style={{ color: "var(--txt3)", width: 56 }}>{r.id}</span>
      <span style={{ width: 120, color: "var(--txt2)" }}>{r.marca}</span>
      <b style={{ flex: 1, minWidth: 0 }}>{r.veiculo}</b>
      <span className="mono" style={{ width: 90, color: "var(--txt3)" }}>{r.fipeCodigo}</span>
      <span style={{ width: 90, color: "var(--txt3)" }}>{r.anoIni}–{r.anoFim}</span>
      <span style={{ width: 70, color: r.conferido ? "var(--ok)" : "var(--txt3)" }}>
        {r.conferido ? "tratado" : "pendente"}
      </span>
    </div>
  )
}

function Divisao({ id, aoFechar, aoAvisar }: {
  id: number
  aoFechar: () => void
  aoAvisar: (m: string | null) => void
}) {
  const candidato = usarCandidatoDaBarra(id)
  const confirmar = usarConfirmarBarra()
  const [variantes, setVariantes] = useState<VarianteDaBarra[]>([])

  // A proposta chega do servidor; o que a pessoa edita é a cópia local.
  useEffect(() => { setVariantes(candidato.data?.variantes ?? []) }, [candidato.data])

  const c = candidato.data

  return (
    <aside style={{ flex: "0 0 420px", width: 420, overflowY: "auto", background: "var(--surface)" }}>
      <div style={{
        position: "sticky", top: 0, background: "var(--surface)", zIndex: 1,
        display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
        borderBottom: "1px solid var(--line2)",
      }}>
        <span className="mono" style={{ fontSize: 12, fontWeight: 600 }}>#{id}</span>
        <span style={{ fontSize: 11, color: "var(--txt2)" }}>divisão proposta</span>
        <button onClick={aoFechar} aria-label="Fechar" style={{
          marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer",
          color: "var(--txt3)", fontSize: 16, lineHeight: 1,
        }}>×</button>
      </div>

      <div style={{ padding: 14 }}>
        {candidato.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Montando a proposta…</p>
        ) : candidato.error ? (
          <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(candidato.error)}</p>
        ) : !c ? null : (
          <>
            <p style={{ margin: "0 0 4px", fontSize: 12.5, fontWeight: 600 }}>{c.pai.veiculo}</p>
            <p style={{ margin: "0 0 12px", fontSize: 11, color: "var(--txt2)" }}>
              {c.pai.marca} · {c.pai.fipeCodigo} · {c.pai.anoIni}–{c.pai.anoFim} ·{" "}
              {c.qtdBarra} parte(s)
            </p>

            {!c.continuar && (
              <Aviso>
                A API não conseguiu propor a divisão deste registro — o texto não se separa pela
                barra de um jeito que dê variantes. Este é para arrumar à mão na consulta da Fipe.
              </Aviso>
            )}

            {/* A chave e o indice, nao o `tabelaFipeId`: variante nova vem com id 0,
                e duas novas no mesmo registro teriam a mesma chave. */}
            {variantes.map((v, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <label style={{ display: "block", fontSize: 10.5, color: "var(--txt2)", marginBottom: 3 }}>
                  Variante {i + 1} ·{" "}
                  {v.tabelaFipeId > 0
                    ? <>registro {v.tabelaFipeId}</>
                    : <span style={{ color: "var(--ok)" }}>registro novo</span>}
                </label>
                <input
                  value={v.veiculo}
                  onChange={(e) => setVariantes(variantes.map((x, j) =>
                    j === i ? { ...x, veiculo: e.target.value } : x))}
                  style={{ ...estiloCampo, width: "100%" }}
                />
              </div>
            ))}

            {c.continuar && variantes.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                <Botao
                  destaque
                  ocupado={confirmar.isPending}
                  aoClicar={() => {
                    aoAvisar(null)
                    confirmar.mutate({ idBarra: id, variantes }, {
                      onSuccess: () => { aoAvisar(`Registro ${id} dividido em ${variantes.length}.`); aoFechar() },
                      onError: (e) => aoAvisar(mensagem(e)),
                    })
                  }}
                >
                  {confirmar.isPending ? "Gravando…" : `Confirmar ${variantes.length} variante(s)`}
                </Botao>
                <Botao aoClicar={aoFechar}>Cancelar</Botao>
              </div>
            )}

            <p style={{ margin: "10px 0 0", fontSize: 10, color: "var(--txt3)", lineHeight: 1.5 }}>
              Até apertar Confirmar, nada foi gravado — esta é a proposta da API, e o texto de cada
              variante é editável porque a divisão automática erra em nome com número.
            </p>
          </>
        )}
      </div>
    </aside>
  )
}

// ------------------------------------------------------------------ campos

const CAMPO_VAZIO = { campo: "", texto: "", retorno: "" }

function Campos() {
  const lista = usarCamposDaFipe()
  const salvar = usarSalvarCampoDaFipe()
  const excluir = usarExcluirCampoDaFipe()

  const [form, setForm] = useState<typeof CAMPO_VAZIO & { id?: number }>(CAMPO_VAZIO)
  const [recado, setRecado] = useState<string | null>(null)
  const [grupo, setGrupo] = useState("")

  const itens = lista.data ?? []

  const grupos = useMemo(() => {
    const m = new Map<string, number>()
    for (const c of itens) m.set(c.campo, (m.get(c.campo) ?? 0) + 1)
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], "pt-BR"))
  }, [itens])

  const visiveis = grupo ? itens.filter((c) => c.campo === grupo) : itens

  function gravar() {
    setRecado(null)
    if (!form.campo.trim() || !form.texto.trim()) {
      setRecado("Campo e texto são obrigatórios — é o par que a normalização procura.")
      return
    }
    salvar.mutate({ id: form.id, dados: { campo: form.campo, texto: form.texto, retorno: form.retorno } }, {
      onSuccess: () => { setRecado(form.id ? "Alterado." : "Incluído."); setForm(CAMPO_VAZIO) },
      onError: (e) => setRecado(mensagem(e)),
    })
  }

  return (
    <>
      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
        padding: "12px 18px", borderBottom: "1px solid var(--line2)",
      }}>
        <Campo rotulo="Campo" ajuda="ciclo, cilindrada, perfil, tracao, transmissao, turbo, valvulas">
          <input value={form.campo} onChange={(e) => setForm({ ...form, campo: e.target.value })}
                 list="campos-da-fipe" style={{ ...estiloCampo, width: 150 }} />
          <datalist id="campos-da-fipe">
            {grupos.map(([g]) => <option key={g} value={g} />)}
          </datalist>
        </Campo>
        <Campo rotulo="Texto no veículo" ajuda="O que aparece escrito na descrição.">
          <input value={form.texto} onChange={(e) => setForm({ ...form, texto: e.target.value })}
                 style={{ ...estiloCampo, width: 200 }} />
        </Campo>
        <Campo rotulo="Vira" ajuda="O valor que fica gravado no campo.">
          <input value={form.retorno} onChange={(e) => setForm({ ...form, retorno: e.target.value })}
                 style={{ ...estiloCampo, width: 200 }} />
        </Campo>
        <Botao destaque aoClicar={gravar} ocupado={salvar.isPending}>
          {form.id ? "Gravar" : "Incluir"}
        </Botao>
        {form.id && <Botao aoClicar={() => setForm(CAMPO_VAZIO)}>Cancelar</Botao>}
      </div>

      {recado && <div style={{ padding: "8px 18px 0" }}><Aviso>{recado}</Aviso></div>}

      <div style={{
        display: "flex", gap: 5, flexWrap: "wrap", padding: "10px 18px 0",
      }}>
        <Filtro rotulo={`todos (${itens.length})`} ativo={grupo === ""} aoClicar={() => setGrupo("")} />
        {grupos.map(([g, n]) => (
          <Filtro key={g} rotulo={`${g} (${n})`} ativo={grupo === g} aoClicar={() => setGrupo(g)} />
        ))}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 14 }}>
        {lista.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Carregando…</p>
        ) : (
          <div style={{ border: "1px solid var(--line2)", borderRadius: 6, overflow: "hidden" }}>
            {visiveis.map((c) => <LinhaDeCampo key={c.id} c={c}
              aoEditar={() => setForm({ ...c })}
              aoExcluir={() => excluir.mutate(c.id, {
                onSuccess: () => setRecado(`Regra ${c.id} excluída.`),
                onError: (e) => setRecado(mensagem(e)),
              })} />)}
          </div>
        )}
      </div>

      <BarraDeEstado direita={`${visiveis.length} de ${itens.length}`}>
        É este dicionário que a normalização usa para preencher os campos derivados.
      </BarraDeEstado>
    </>
  )
}

function LinhaDeCampo({ c, aoEditar, aoExcluir }: {
  c: CampoDaFipe
  aoEditar: () => void
  aoExcluir: () => void
}) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "5px 10px",
      borderBottom: "1px solid var(--line2)", fontSize: 11.5,
    }}>
      <span className="mono" style={{ color: "var(--txt3)", width: 52 }}>{c.id}</span>
      <span style={{ width: 110, color: "var(--txt2)" }}>{c.campo}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{c.texto}</span>
      <span style={{ color: "var(--txt3)" }}>→</span>
      <b style={{ width: 200 }}>{c.retorno || <i style={{ color: "var(--txt3)" }}>(vazio)</i>}</b>
      <button onClick={aoEditar} style={acao}>alterar</button>
      <button onClick={aoExcluir} style={acao}>excluir</button>
    </div>
  )
}

function Filtro({ rotulo, ativo, aoClicar }: { rotulo: string; ativo: boolean; aoClicar: () => void }) {
  return (
    <button onClick={aoClicar} style={{
      padding: "3px 9px", fontSize: 11, borderRadius: 11, cursor: "pointer",
      border: "1px solid " + (ativo ? "var(--acao)" : "var(--line)"),
      background: ativo ? "var(--surface2)" : "transparent",
      color: ativo ? "var(--txt)" : "var(--txt2)",
    }}>
      {rotulo}
    </button>
  )
}

// ------------------------------------------------------------------ veículo/ano

/**
 * O atalho do sistema antigo: em vez de descer pelos filtros em cascata, dizer
 * marca, nome curto e ano. É a pergunta de quem já sabe o que procura.
 */
function VeiculoAno() {
  const [marca, setMarca] = useState("")
  const [nomeCurto, setNomeCurto] = useState("")
  const [ano, setAno] = useState(0)
  const [pedido, setPedido] = useState({ marca: "", nomeCurto: "", ano: 0 })

  const consulta = usarFipePorVeiculoAno(pedido)
  const itens = consulta.data?.itens ?? []

  return (
    <>
      <div style={{
        display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end",
        padding: "12px 18px", borderBottom: "1px solid var(--line2)",
      }}>
        <Campo rotulo="Marca">
          <input value={marca} onChange={(e) => setMarca(e.target.value)}
                 style={{ ...estiloCampo, width: 160 }} placeholder="AUDI" />
        </Campo>
        <Campo rotulo="Nome curto" ajuda="O nome que a normalização calcula — “A6”, não “A6 2.8 30V”.">
          <input value={nomeCurto} onChange={(e) => setNomeCurto(e.target.value)}
                 style={{ ...estiloCampo, width: 180 }} placeholder="A6" />
        </Campo>
        <Campo rotulo="Ano">
          <input type="number" value={ano || ""} onChange={(e) => setAno(Number(e.target.value))}
                 style={{ ...estiloCampo, width: 100 }} placeholder="2000" />
        </Campo>
        <Botao destaque aoClicar={() => setPedido({ marca, nomeCurto, ano })}
               ocupado={consulta.isFetching}>
          Procurar
        </Botao>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 14 }}>
        {!pedido.marca && !pedido.nomeCurto && !pedido.ano ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)", maxWidth: "60ch", lineHeight: 1.6 }}>
            Preencha ao menos um campo. O ano casa com a faixa do registro — um veículo de
            1996 a 2001 aparece quando se procura por 2000.
          </p>
        ) : consulta.isPending ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Procurando…</p>
        ) : consulta.error ? (
          <p style={{ fontSize: 12.5, color: "var(--erro)" }}>{mensagem(consulta.error)}</p>
        ) : itens.length === 0 ? (
          <p style={{ fontSize: 12.5, color: "var(--txt3)" }}>Nenhum registro com essa combinação.</p>
        ) : (
          <div style={{ border: "1px solid var(--line2)", borderRadius: 6, overflow: "hidden" }}>
            {itens.map((r) => (
              <div key={r.id} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "5px 10px",
                borderBottom: "1px solid var(--line2)", fontSize: 11.5,
              }}>
                <span className="mono" style={{ color: "var(--txt3)", width: 56 }}>{r.id}</span>
                <span style={{ width: 110, color: "var(--txt2)" }}>{r.marca}</span>
                <b style={{ flex: 1, minWidth: 0 }}>{r.veiculo}</b>
                <span style={{ width: 120, color: "var(--txt2)" }}>{r.nomeCurto}</span>
                <span style={{ width: 90, color: "var(--txt3)" }}>{r.anoIni}–{r.anoFim}</span>
                <span style={{ width: 90, color: "var(--txt3)" }}>{r.combustivel}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <BarraDeEstado direita={itens.length > 0 ? `${itens.length} registro(s)` : undefined}>
        Atalho do sistema antigo, para quem já sabe o que procura.
      </BarraDeEstado>
    </>
  )
}

const acao: React.CSSProperties = {
  border: 0, background: "transparent", padding: 0, cursor: "pointer",
  fontSize: 10.5, color: "var(--txt2)", textDecoration: "underline",
}
